import { useEffect, useRef, useState } from 'react';
import { mediaQueries, css } from '@eti/styles';
import { useSnowplowTracking, useTranslation, useProperty } from '@eti/providers';
import isFunction from 'lodash/isFunction';
import { AgeType } from '@eti/schema-types';
import { price as priceUtils } from '@eti/utils';
import { Button, Inline, vars } from '@etg/wings';
import Media from 'react-media';
import { BUNDLE_PRODUCT, FORM_SECTION } from '../../utils/constants';
import { getBundlePosition, getRowsData } from '../../utils/utils';
import { TRACKING_PROPERTIES, BUNDLE_VARIANTS, useProductSnowplowTracking } from '../../../custom-hooks/useProductSnowplowTracking';
import BundleHeader from './BundleHeader';
import BundleModal from './BundleModal';
import BundleRows from './BundleRows';
import BundleSelection from './BundleSelection';
import MobileBundle from './MobileBundle';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const {
  BUNDLE_TD,
  BUNDLE_YST
} = BUNDLE_VARIANTS;
const wrapperStyles = css`
  background-color: #fff;
  border-radius: ${vars.card.borderRadius};
  padding: 16px 16px 0;
`;
const footerWrapperStyles = css`
  padding: 16px;

  label:first-of-type {
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
`;
const Bundle = _ref => {
  var _travelers$filter$len;
  let {
    ancillaryBundleId,
    confirmButtonText,
    discountPercentageTitle,
    isTDOrStepPage,
    isYSTPage,
    name,
    noExtrasText,
    onChange,
    onConfirm,
    options = [],
    priceText,
    productPosition,
    recommendedText,
    selectedValue,
    shouldShowConfirmationButton,
    shouldShowPricePerPerson,
    title: bundleTitle,
    travelers
  } = _ref;
  const [selectedBundleToOpen, setSelectedBundleToOpen] = useState(null);
  const {
    t
  } = useTranslation();
  const {
    p
  } = useProperty();
  const rootElement = useRef(null);
  const {
    isSnowplowTrackingEnabled,
    sendTrackEvent
  } = useSnowplowTracking();
  const {
    isFullyVisible,
    sendAddToCartEvent,
    sendClickEvent,
    sendProductSeenEvent,
    updateContext
  } = useProductSnowplowTracking(isYSTPage ? BUNDLE_YST : BUNDLE_TD, {
    rootElement,
    productPosition,
    sendSeenEvent: false
  });
  const [emptyOption, ...sellableOptions] = options;
  const {
    id: emptyOptionId
  } = emptyOption;
  const bundleName = `productBundle-${name}`;
  const titles = sellableOptions.length > 0 ? sellableOptions.map(option => option.title) : [];
  const emptyOptionTitle = t('Product.Bundle.Selection.Empty.Option.Label');
  const travelersWithoutInfants = (_travelers$filter$len = travelers === null || travelers === void 0 ? void 0 : travelers.filter(traveler => traveler.ageType !== AgeType.Inf).length) !== null && _travelers$filter$len !== void 0 ? _travelers$filter$len : 0;
  const bundlePosition = isYSTPage ? getBundlePosition(options, selectedValue && selectedValue.value) : productPosition;
  useEffect(() => {
    const handleSeenEvent = async () => {
      await updateContext({
        position: bundlePosition
      });
      sendProductSeenEvent({
        property: TRACKING_PROPERTIES.ON_PAGE,
        value: ancillaryBundleId
      });
    };
    if (isFullyVisible) {
      handleSeenEvent();
    }
  }, [ancillaryBundleId, bundlePosition, isFullyVisible, sendProductSeenEvent, updateContext]);
  const handleConfirmation = () => {
    if (isFunction(onConfirm)) {
      onConfirm(selectedValue);
      if (isSnowplowTrackingEnabled) {
        sendTrackEvent({
          category: 'Results',
          action: 'Click',
          label: 'Bundle continue button'
        });
      }
    }
  };
  const handleModalOpen = id => {
    const infoIcon = document.getElementById(`${BUNDLE_PRODUCT}.${id}`);
    if (infoIcon) {
      infoIcon.blur();
    }
    setSelectedBundleToOpen(`${BUNDLE_PRODUCT}.${id}`);
  };
  const handleModalClose = id => {
    const infoIcon = document.getElementById(id);
    if (infoIcon) {
      infoIcon.focus();
    }
    setSelectedBundleToOpen(null);
  };
  const handleAddToCartEventOnTDPage = async (selectionValue, selectionOptionPrice, selectionOptionType) => {
    if (selectionValue === emptyOptionId) {
      await updateContext({
        position: productPosition,
        quantity: travelersWithoutInfants
      });
      sendAddToCartEvent({
        addToCart: false,
        property: TRACKING_PROPERTIES.ON_PAGE,
        value: ancillaryBundleId
      });
    } else {
      await updateContext({
        position: productPosition,
        quantity: travelersWithoutInfants,
        price: priceUtils.formatPrice(selectionOptionPrice)
      });
      sendAddToCartEvent({
        addToCart: true,
        label: `${BUNDLE_TD}: ${selectionOptionType}`,
        property: TRACKING_PROPERTIES.ON_PAGE,
        value: selectionValue
      });
    }
  };
  const handleChange = async event => {
    const field = `${FORM_SECTION}.value`;
    const selectionValue = event.target.value;
    const {
      type: selectionOptionType,
      priceRaw: selectionOptionPrice
    } = options.find(_ref2 => {
      let {
        id
      } = _ref2;
      return id === selectionValue;
    }) || {};
    if (isYSTPage) {
      await updateContext({
        position: getBundlePosition(options, selectionOptionType || null),
        quantity: travelersWithoutInfants,
        price: priceUtils.formatPrice(selectionOptionPrice)
      });
      sendClickEvent({
        label: `${BUNDLE_YST}: ${selectionOptionType}`,
        property: TRACKING_PROPERTIES.ON_PAGE,
        value: ancillaryBundleId
      });
    } else {
      handleAddToCartEventOnTDPage(selectionValue, selectionOptionPrice || 0, selectionOptionType);
    }
    if (isFunction(onChange)) {
      onChange({
        value: selectionValue
      }, field);
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    className: wrapperStyles,
    "data-testid": bundleName,
    ref: rootElement,
    children: [/*#__PURE__*/_jsx(BundleModal, {
      isTDOrStepPage: isTDOrStepPage,
      noExtrasText: noExtrasText,
      onChange: onChange,
      onClose: () => handleModalClose(selectedBundleToOpen),
      options: sellableOptions,
      selectedBundleToOpen: selectedBundleToOpen,
      setSelectedBundleToOpen: setSelectedBundleToOpen,
      shouldShow: Boolean(selectedBundleToOpen),
      titles: titles
    }), /*#__PURE__*/_jsx(Media, {
      query: mediaQueries.medium.up,
      children: isDesktop => isDesktop ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(BundleHeader, {
          bundleTitle: bundleTitle,
          discountPercentageTitle: discountPercentageTitle,
          handleModalOpen: handleModalOpen,
          isOpen: Boolean(selectedBundleToOpen),
          recommendedText: recommendedText,
          sellableOptions: sellableOptions
        }), /*#__PURE__*/_jsx(BundleRows, {
          rowsData: getRowsData(sellableOptions)
        }), /*#__PURE__*/_jsx(BundleSelection, {
          discountPercentageTitle: discountPercentageTitle,
          emptyOptionId: emptyOptionId,
          emptyOptionTitle: emptyOptionTitle,
          footerWrapperStyles: footerWrapperStyles,
          handleChange: handleChange,
          name: name,
          priceText: priceText,
          sellableOptions: sellableOptions,
          shouldShowPricePerPerson: shouldShowPricePerPerson,
          travelers: travelers
        })]
      }) : /*#__PURE__*/_jsx(MobileBundle, {
        discountPercentageTitle: discountPercentageTitle,
        emptyOptionId: emptyOptionId,
        emptyOptionTitle: emptyOptionTitle,
        footerWrapperStyles: footerWrapperStyles,
        handleChange: handleChange,
        handleModalOpen: handleModalOpen,
        isOpen: Boolean(selectedBundleToOpen),
        name: name,
        priceText: priceText,
        sellableOptions: sellableOptions,
        shouldShowPricePerPerson: shouldShowPricePerPerson,
        travelers: travelers
      })
    }), shouldShowConfirmationButton && /*#__PURE__*/_jsx(Inline, {
      align: "end",
      className: footerWrapperStyles,
      children: /*#__PURE__*/_jsx(Button, {
        "data-testid": `${bundleName}-confirmation-button`,
        isDisabled: p('IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled') ? !(selectedValue !== null && selectedValue !== void 0 && selectedValue.value) : false,
        onClick: handleConfirmation,
        variant: "primary",
        children: confirmButtonText
      })
    })]
  });
};
export default Bundle;
//# sourceMappingURL=Bundle.js.map